import type { Item } from "@/shared/components/generic/items.ts";
import type { DailyTag } from "@/pages/human-resources/time-management/business/daily-tag/DailyTag.ts";
import { useTranslation } from "react-i18next";
import useScheduleReferenceData from "@/pages/human-resources/time-management/schedules/reference-provider/useScheduleReferenceData.ts";
import useDisplayOptionsState from "@/pages/human-resources/time-management/model/display-options/useDisplayOptionsState.ts";
import useDisplayOptionsDispatch from "@/pages/human-resources/time-management/model/display-options/useDisplayOptionsDispatch.ts";
import { useCallback, useMemo, useState } from "react";

interface Controller {
  title: string;
  items: Item<DailyTag>[];
  isSelectedItem: (item: Item<DailyTag>) => boolean;
  toggleItem: (item: Item<DailyTag>) => void;
  selectAll: () => void;
  selectNone: () => void;
  folded: boolean;
  setFolded: (f: boolean) => void;
}

export default function useDailyTagsSelectorController(): Controller {
  const { t } = useTranslation(["common", "humanResources"]);
  const referenceData = useScheduleReferenceData();
  const { selectedDailyTags } = useDisplayOptionsState();
  const { dispatch } = useDisplayOptionsDispatch();
  const dailyTags = useMemo(() => {
    return referenceData.listDailyTags();
  }, [referenceData]);

  const [folded, setFolded] = useState(true);

  const title = useMemo(() => {
    const label = t(
      "humanResources:timeManagement.scheduleDisplayOptions.selectedDailyTags.title",
    );
    const total = referenceData.listDailyTags().length;
    const nbSelected = selectedDailyTags.count();
    return `${label} (${nbSelected}/${total})`;
  }, [referenceData, selectedDailyTags, t]);

  const items: Item<DailyTag>[] = dailyTags.map((dt) => ({
    key: dt.id,
    label: dt.label,
    data: dt,
  }));

  const isSelectedItem = useCallback(
    (item: Item<DailyTag>) => {
      if (item.data === undefined) return false;
      return selectedDailyTags.isSelected(item.data);
    },
    [selectedDailyTags],
  );

  const toggleItem = useCallback(
    (item: Item<DailyTag>): void => {
      if (item.data === undefined) return;
      dispatch({ type: "toggleDailyTag", dailyTag: item.data });
    },
    [dispatch],
  );

  const selectAll = useCallback((): void => {
    dispatch({ type: "setDailyTags", dailyTags });
  }, [dispatch, dailyTags]);

  const selectNone = useCallback((): void => {
    dispatch({ type: "clearDailyTags" });
  }, [dispatch]);

  return {
    title,
    items,
    isSelectedItem,
    toggleItem,
    selectAll,
    selectNone,
    folded,
    setFolded,
  };
}
